
import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useGamificationContext } from '../../context/GamificationContext';
import { API_URL } from '../../config';
import SiteLayout from '../../layouts/SiteLayout';
import Header from '../../components/Header/Header';
import Box from '../../components/Common/Box';
import MobileBriefing from './MobileBriefing';
import { useScreenType } from '../../hooks/useScreenType';
import '../../styles/responsive.css';
import './MarketBriefingScreen.css';

interface KeyMetrics {
    brvm_c: { value: number; change: number };
    brvm_30: { value: number; change: number };
    brvm_prestige?: { value: number; change: number };
    volume: number;
    cap: number;
    top_gainers: Array<{ symbol: string; change: number }>;
    top_losers: Array<{ symbol: string; change: number }>;
    most_active: Array<{ symbol: string; value: number }>;
}

interface Briefing {
    id: number;
    date_briefing: string;
    market_mood: string;
    content_html: string;
    key_metrics: string; // JSON string
    created_at: string;
    whatsapp_content?: string;
}

const MarketBriefingScreen: React.FC = () => {
    const { token, user, hasPermission } = useAuth();
    const { trackAction } = useGamificationContext();
    const { isMobile } = useScreenType();
    const [briefing, setBriefing] = useState<Briefing | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string>('');
    const [metrics, setMetrics] = useState<KeyMetrics | null>(null);
    const [downloadingPdf, setDownloadingPdf] = useState<boolean>(false);
    const [whatsappCopied, setWhatsappCopied] = useState<boolean>(false);

    const isAdmin = user?.role?.name === 'global_admin' || hasPermission('admin_platform');

    useEffect(() => {
        if (token) {
            trackAction('VIEW_FLASH_MARKET');
            fetchLatestBriefing();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token]);

    const fetchLatestBriefing = async () => {
        try {
            setLoading(true);
            const response = await fetch(`${API_URL}/api/reports/briefings/latest`, {
                headers: { Authorization: `Bearer ${token}` },
            });

            if (response.ok) {
                const data: Briefing = await response.json();
                setBriefing(data);
                if (data.key_metrics) {
                    try {
                        setMetrics(JSON.parse(data.key_metrics));
                    } catch (e) {
                        console.error("Error parsing metrics", e);
                    }
                }
            } else {
                if (response.status === 404) {
                    setError("Le briefing d'aujourd'hui n'est pas encore disponible.");
                } else {
                    setError("Impossible de charger le briefing.");
                }
            }
        } catch (err) {
            console.error(err);
            setError("Erreur réseau.");
        } finally {
            setLoading(false);
        }
    };

    const generateBriefing = async () => {
        try {
            setLoading(true);
            const response = await fetch(`${API_URL}/api/reports/briefings/generate`, {
                method: 'POST',
                headers: { Authorization: `Bearer ${token}` },
            });
            if (response.ok) {
                await fetchLatestBriefing();
            }
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
        }
    };

    const downloadPDF = async () => {
        if (!briefing) return;

        try {
            setDownloadingPdf(true);
            const dateStr = briefing.date_briefing; // Already in YYYY-MM-DD format

            const response = await fetch(
                `${API_URL}/api/reports/briefings/${dateStr}/pdf`,
                { headers: { Authorization: `Bearer ${token}` } }
            );

            if (!response.ok) throw new Error('PDF download failed');

            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `briefing_kanea_${dateStr}.pdf`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('PDF download error:', error);
            // Show error via button text change or similar mechanism if possible, 
            // but for now console log is better than annoying alert. 
            // We can add a temporary error state if we want visual feedback.
            setError("Erreur téléchargement PDF");
            setTimeout(() => setError(''), 3000);
        } finally {
            setDownloadingPdf(false);
        }
    };

    const handleWhatsAppShare = () => {
        if (briefing?.whatsapp_content) {
            navigator.clipboard.writeText(briefing.whatsapp_content);
            setWhatsappCopied(true);
            setTimeout(() => setWhatsappCopied(false), 2000);
            
            // Open WhatsApp share link
            const url = `https://api.whatsapp.com/send?text=${encodeURIComponent(briefing.whatsapp_content)}`;
            window.open(url, '_blank');
        }
    };

    if (loading && !briefing) {
        return (
            <SiteLayout>
                <Header icon="mail" title="Flash Marché" />
                <div style={{ padding: '20px', textAlign: 'center' }}>Chargement...</div>
            </SiteLayout>
        );
    }

    return (
        <SiteLayout>
            <Header icon="mail" title="Flash Marché" />

            {error && !briefing && (
                <Box>
                    <div style={{ padding: '20px', textAlign: 'center' }}>
                        <p style={{ marginBottom: '15px' }}>{error}</p>
                        <button
                            onClick={generateBriefing}
                            style={{
                                padding: '8px 16px',
                                backgroundColor: '#2196F3',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer'
                            }}
                        >
                            Générer le briefing maintenant (Demo)
                        </button>
                    </div>
                </Box>
            )}

            {briefing && metrics && (
                isMobile ? (
                    <MobileBriefing
                        briefing={briefing}
                        metrics={metrics}
                        downloadPDF={downloadPDF}
                        downloadingPdf={downloadingPdf}
                        isAdmin={isAdmin}
                        onWhatsAppShare={handleWhatsAppShare}
                        whatsappCopied={whatsappCopied}
                    />
                ) : (
                    <div className="responsive-grid">
                        <div className="col-span-12">
                            {/* Header Date & Mood */}
                            <div className="briefing-header-strip" style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                marginBottom: '20px',
                                flexWrap: 'wrap',
                                gap: '10px'
                            }}>
                                <h2 style={{ margin: 0 }}>
                                    📅 {new Date(briefing.date_briefing).toLocaleDateString('fr-FR', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                                </h2>
                                <div className="briefing-header-actions" style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                                    <span style={{
                                        padding: '6px 12px',
                                        borderRadius: '20px',
                                        fontWeight: 'bold',
                                        backgroundColor: briefing.market_mood === 'Bullish' ? 'rgba(34, 197, 94, 0.1)' : briefing.market_mood === 'Bearish' ? 'rgba(239, 68, 68, 0.1)' : 'rgba(156, 163, 175, 0.1)',
                                        color: briefing.market_mood === 'Bullish' ? '#22c55e' : briefing.market_mood === 'Bearish' ? '#ef4444' : 'var(--text-muted)'
                                    }}>
                                        Ambiance: {briefing.market_mood === 'Bullish' ? '🟢 Haussière' : briefing.market_mood === 'Bearish' ? '🔴 Baissière' : '⚪ Neutre'}
                                    </span>
                                    {isAdmin && briefing.whatsapp_content && (
                                        <button
                                            onClick={handleWhatsAppShare}
                                            style={{
                                                padding: '8px 16px',
                                                borderRadius: '8px',
                                                border: '1px solid #25d366',
                                                background: whatsappCopied ? '#22c55e' : 'rgba(37, 211, 102, 0.1)',
                                                color: whatsappCopied ? 'white' : '#25d366',
                                                fontWeight: '600',
                                                cursor: 'pointer',
                                                transition: 'all 0.2s',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '6px'
                                            }}
                                        >
                                            {whatsappCopied ? '✅ Copié !' : '📱 Partager WhatsApp'}
                                        </button>
                                    )}
                                    <button
                                        onClick={downloadPDF}
                                        disabled={downloadingPdf}
                                        style={{
                                            padding: '8px 16px',
                                            borderRadius: '8px',
                                            border: 'none',
                                            background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                                            color: 'white',
                                            fontWeight: '600',
                                            cursor: downloadingPdf ? 'not-allowed' : 'pointer',
                                            opacity: downloadingPdf ? 0.6 : 1,
                                            transition: 'all 0.2s'
                                        }}
                                    >
                                        {downloadingPdf ? '⏳ Téléchargement...' : '📄 Télécharger PDF'}
                                    </button>
                                </div>
                            </div>
                        </div>

                        {/* TOP INDICATORS BAND */}
                        <div className="col-span-12">
                            <Box>
                                <div className="briefing-indicators" style={{
                                    display: 'flex',
                                    flexWrap: 'wrap',
                                    justifyContent: 'space-around',
                                    padding: '15px',
                                    borderBottom: '1px solid #eee'
                                }}>
                                    {/* BRVM C */}
                                    <div className="briefing-indicator-tile" style={{ textAlign: 'center', minWidth: '120px', margin: '10px' }}>
                                        <div style={{ color: '#666', fontSize: '12px', textTransform: 'uppercase' }}>BRVM Composite</div>
                                        <div style={{ fontSize: '24px', fontWeight: 'bold' }}>{metrics.brvm_c.value}</div>
                                        <div style={{
                                            color: metrics.brvm_c.change >= 0 ? '#4CAF50' : '#f44336',
                                            fontWeight: '500'
                                        }}>
                                            {metrics.brvm_c.change >= 0 ? '+' : ''}{metrics.brvm_c.change}%
                                        </div>
                                    </div>

                                    {/* BRVM Prestige */}
                                    {metrics.brvm_prestige && (
                                        <div className="briefing-indicator-tile" style={{ textAlign: 'center', minWidth: '120px', margin: '10px' }}>
                                            <div style={{ color: '#666', fontSize: '12px', textTransform: 'uppercase' }}>BRVM Prestige</div>
                                            <div style={{ fontSize: '24px', fontWeight: 'bold' }}>{metrics.brvm_prestige.value}</div>
                                            <div style={{
                                                color: metrics.brvm_prestige.change >= 0 ? '#4CAF50' : '#f44336',
                                                fontWeight: '500'
                                            }}>
                                                {metrics.brvm_prestige.change >= 0 ? '+' : ''}{metrics.brvm_prestige.change}%
                                            </div>
                                        </div>
                                    )}

                                    {/* Volume */}
                                    <div className="briefing-indicator-tile" style={{ textAlign: 'center', minWidth: '120px', margin: '10px' }}>
                                        <div style={{ color: '#666', fontSize: '12px', textTransform: 'uppercase' }}>Volume Échangé</div>
                                        <div style={{ fontSize: '24px', fontWeight: 'bold' }}>{(metrics.volume / 1000000).toFixed(0)} M</div>
                                        <div style={{ color: '#999', fontSize: '12px' }}>FCFA</div>
                                    </div>

                                    {/* Tops */}
                                    <div className="briefing-top-tile" style={{ minWidth: '150px', margin: '10px' }}>
                                        <div style={{ color: '#4CAF50', fontSize: '12px', fontWeight: 'bold', marginBottom: '5px' }}>🚀 TOPS</div>
                                        {metrics.top_gainers.slice(0, 3).map((t, idx) => (
                                            <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px' }}>
                                                <span>{t.symbol}</span>
                                                <span style={{ color: '#4CAF50', fontWeight: 'bold' }}>+{t.change}%</span>
                                            </div>
                                        ))}
                                    </div>

                                    {/* Flops */}
                                    <div className="briefing-top-tile" style={{ minWidth: '150px', margin: '10px' }}>
                                        <div style={{ color: '#f44336', fontSize: '12px', fontWeight: 'bold', marginBottom: '5px' }}>📉 FLOPS</div>
                                        {metrics.top_losers.slice(0, 3).map((t, idx) => (
                                            <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px' }}>
                                                <span>{t.symbol}</span>
                                                <span style={{ color: '#f44336', fontWeight: 'bold' }}>{t.change}%</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </Box>
                        </div>

                        {/* AI CONTENT */}
                        <div className="col-span-12" style={{ marginTop: '20px' }}>
                            <Box>
                                <div className="box-content box-horizontal-padding box-vertical-padding">
                                    <div
                                        className="briefing-content"
                                        dangerouslySetInnerHTML={{ __html: briefing.content_html }}
                                    />
                                    <div style={{
                                        marginTop: '20px',
                                        paddingTop: '15px',
                                        borderTop: '1px solid #f1f5f9',
                                        fontSize: '12px',
                                        color: '#94a3b8',
                                        fontStyle: 'italic',
                                        textAlign: 'center'
                                    }}>
                                        Ce briefing est généré quotidiennement par Nia, notre intelligence artificielle, et revu par notre équipe d'analystes après la clôture du marché.
                                    </div>
                                </div>
                            </Box>
                        </div>
                    </div>
                )
            )}
        </SiteLayout>
    );
};

export default MarketBriefingScreen;
